import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { getAllParents } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { isNumeric } from "@cervval.js/common/src/tools/number.tools";
import { compare, compareAsNumber } from "@cervval.js/common/src/tools/object.tools";
import { CollectionTools, anyReallyAny } from "@cervval.js/common/tools";
import { COLUMN_UID_ATTRIBUTE, CjsSmartTable, RenderCacheData, SmartTableColumn, SmartTableRow } from "./smartTable";

/** Sort directions available */
export type SmartTableSortsAvailable = "none" | "ascending" | "descending";

/** Active column sort type*/
export type ColumnSorted<ItemData> = { column: SmartTableColumn<ItemData>, sortType: SmartTableSortsAvailable, element: HTMLElement };

/** Details of event when click sort div */
export interface SmartTableSortClickEvent<ItemData> {
    /** Column where the click has been trigerred from */
    col: SmartTableColumn<ItemData>;

    /** Mouse event */
    mouseEvent: MouseEvent;
}

export class SmartTableSortHelper<ItemData> {

    @Injected(LOG_SERVICE, { optional: true })
    private readonly _log?: LogService;

    /**
     * Event dispatched when a row is clicked
     */
    @Event(NoBubblesEventInit)
    public sortClickEvent!: EventHandler<SmartTableSortClickEvent<ItemData>>;

    /** Sort is currently occurring, used to disable multiple sorts calls */
    protected _sortIsOccurring = false;

    /** Available sorts for columns */
    protected _sortsAvailable: SmartTableSortsAvailable[] = ["none", "ascending", "descending"];

    /** Array of columns currently sorted */
    protected _activeSortedColumns: ColumnSorted<ItemData>[] = [];

    /** Getter for _activeSortedColumns */
    public get activeSortedColumns(): ColumnSorted<ItemData>[] { return this._activeSortedColumns; }

    constructor(private _smartTable: CjsSmartTable<ItemData>) { }

    /**
     * Apply different sorts to the smartTable
     * @param p
     * @param p.sorts An array of the sorts we want to apply. Each index is composed of the uid of the column we want to sort, and the sort we want to apply
     * @param p.merge If true, the new sorts are merged with previous active sorts. A sort is either pushed, or if the uid of the column is already present in active sorts, the type of sort is updated.
     * A "none" sort erased the record from active sorts. If false, erase all active sorts and push the sort provided
     * @returns 
     */
    public applySorts(p: { sorts: { uid: string, sortType: SmartTableSortsAvailable }[], merge: boolean }): void {
        if (!p.sorts.length) { // No sorts are provided
            if (p.merge) { return undefined; } // And we keep last configuration, so return immediately
            // Otherwise, erase previous config
            this.resetActiveSortedColumns();
        } else {
            if (!p.merge) { this.resetActiveSortedColumns(); }
            p.sorts.forEach((sort) => {
                // Seek for activeColumn with same uid.
                const previousSortForColumn = this.findActiveColumn(sort.uid);
                if (previousSortForColumn) { // Apply new sort, with special case that if sort equal none, we erase record
                    if (sort.sortType === "none") { CollectionTools.removeFirstFromArray(this._activeSortedColumns, previousSortForColumn); }
                    else { previousSortForColumn.sortType = sort.sortType; }
                } else if (sort.sortType !== "none") {
                    const column = this._smartTable.options.columns.find((col) => col.uid === sort.uid);
                    const ariaElm = column?.headerHtml;
                    if (column && ariaElm) {
                        this._activeSortedColumns.push({ column, sortType: sort.sortType, element: ariaElm });
                    }
                }
            });
        }
        const renderCacheData: RenderCacheData = {};
        this.sortTable({ renderCacheData });
        return this._smartTable.renderHeader(renderCacheData);
    }

    /** Reset array _activeSortedColumns */
    public resetActiveSortedColumns(): void {
        this._activeSortedColumns = [];
    }

    /** Div currently used to sort respective column */
    protected _sortableDivs: HTMLElement[] = [];

    /**
     * Add a div into _sortableDivs
     * @param div Div to add
     */
    public addSortableDiv(div: HTMLElement): void {
        this._sortableDivs.push(div);
    }

    /**
     * Reset array _sortableDivs
     */
    public resetSortableDivs(): void {
        this._sortableDivs = [];
    }

    /**
     * Reset aria-sort attribute on _sortableDivs
     */
    public resetAriaSort(): void {
        this._sortableDivs.forEach((elt) => {
            elt.setAttribute("aria-sort", "none");
            (elt.nextSibling as HTMLElement)?.setAttribute("aria-sort-order", "");
        });
    }

    /**
     * Return a ColumnSorted object if uid provided is include in _activeSortedColumns, undefined otherwise
     * @param uid Uid of the column to seek
     * @returns 
     */
    public findActiveColumn(uid: string): ColumnSorted<ItemData> | undefined {
        return this._activeSortedColumns.find((col) => col.column.uid === uid);
    }

    /**
     * Return a sort order for the column with uid provided
     * @param uid Uid of the column to seek
     * @returns 0 if no sorted columns have the uid provided, a 1-based integer otherwise
     */
    public findSortOrder(uid: string): number {
        const i = this._activeSortedColumns.findIndex((col) => col.column.uid === uid);
        return i > -1 ? i + 1 : 0;
    }

    /**
     * Check if a column should be sorted (from options)
     */
    public columnIsSortable(columnUid: string): boolean {
        const _column = this._smartTable.options.columns.find((col) => col.uid === columnUid);
        if (!_column) { return false; } // Column is not present in smartTable
        if (_column.sortDisabled) { return false; }
        return !this._smartTable.options.disableSort && (!this._smartTable.options.columnsToDisableSort || !this._smartTable.options.columnsToDisableSort?.includes(_column.uid));
    }

    /** Set columns sort order html attribute  */
    public setHeaderSortOrderAttributes(): void {
        this._activeSortedColumns.forEach((sc, index) => {
            const sortOrderElem: HTMLElement | null = sc.element.nextSibling as HTMLElement;
            sortOrderElem?.setAttribute("aria-sort-order", (index + 1).toString());
        });
    }

    /**
     * Sort column values
     * Check if both values are number
     * Default case : compare as both strings
     */
    public sortColumnValue(aValue: anyReallyAny, bValue: anyReallyAny, colum?: SmartTableColumn<ItemData>): number {
        if (!this._smartTable.options.sortNullFirst) {
            // null must be last (this is not the default behavior in object tools compare functions)
            if (aValue == null && bValue != null) {
                return 1;
            } else if (aValue != null && bValue == null) {
                return -1;
            }
        }
        // -- Check if both values are number
        if (isNumeric(aValue) && isNumeric(bValue)) {
            return compareAsNumber(aValue, bValue);
        }
        return this.defaultSortColumnValue(aValue, bValue);
    }

    /**
     * Default sort of a column
     */
    public defaultSortColumnValue(aValue: anyReallyAny, bValue: anyReallyAny): number {
        // Lower case to match filters
        const aLowerCaseValue = String(aValue).toLocaleLowerCase();
        const bLowerCaseValue = String(bValue).toLocaleLowerCase();
        return compare(aLowerCaseValue, bLowerCaseValue);
    }

    /**
     * Sort data model by property linked with columns, clear and fill table body with sorted data model
     */
    public sortTable(params?: { renderCacheData?: RenderCacheData, noRefresh?: boolean, noEmit?: boolean }): void {

        const renderCacheData = params?.renderCacheData ?? {};
        /**
         * Access for property smartTable._tableRows is unorthodox, because this attribute needs to stay private.
         * But we really need it on the sort function. So we access it with a pure JS syntax.
         * In fact, in JS, no attributes are really private (kind of like Python), you can always access it with
         * operator ["name-of-attribute"], with any typing as side-effect. This is why this syntax is used here.
         */
        const tableRowsKey = "_tableRows";
        if (this._smartTable[tableRowsKey] == null) {
            this._log?.error("Attribute " + tableRowsKey + " not set on smartTable. See smartTableSort.helper.ts");
        }

        // Reset to initial rows
        this._smartTable[tableRowsKey] = [...this._smartTable.initialTableRows];

        if (this._activeSortedColumns.length) {
            const compareTwoRowsByColumn: (a: SmartTableRow<ItemData>, b: SmartTableRow<ItemData>, column: SmartTableColumn<ItemData>, ariaSort: SmartTableSortsAvailable) => number = (a, b, column, ariaSort) => {
                let comparatorResult: number;

                // If custom sortComparator is given
                const aData = this._smartTable.getRowItemData(a);
                const bData = this._smartTable.getRowItemData(b);
                const aValue = this._smartTable.getCellByRowAndColumn(a.uid, column.uid, renderCacheData)?.value;
                const bValue = this._smartTable.getCellByRowAndColumn(b.uid, column.uid, renderCacheData)?.value;
                if (column.sortComparator) {
                    comparatorResult = column.sortComparator(aData, aValue, bData, bValue);
                } else {
                    comparatorResult = this.sortColumnValue(aValue, bValue, column);
                }
                return ariaSort === "ascending" ? comparatorResult : -comparatorResult;
            };

            const sortRowsByColumns: (a: SmartTableRow<ItemData>, b: SmartTableRow<ItemData>) => number = (a, b) => {
                for (const sortColumn of this._activeSortedColumns) {
                    const result = compareTwoRowsByColumn(a, b, sortColumn.column, sortColumn.sortType);
                    if (result !== 0) { return result; }
                }
                return 0;
            };

            // Sort data model by property name
            if (this._smartTable.options.hierarchyOptions) {
                // If hierarchy information is available, rows are sorted specifically, parent always displayed above children
                this._smartTable[tableRowsKey] = this._smartTable.hierarchyHelper.sortRowsUsingHierarchy(this._smartTable.tableRows, sortRowsByColumns);
            } else {
                this._smartTable[tableRowsKey].sort(sortRowsByColumns);
            }
        }

        // Update globalIndex with correct order
        this._smartTable[tableRowsKey].forEach((r, i) => r.globalIndex = i);

        this._smartTable.updateDisplayableRows();

        // Reset lead selection
        this._smartTable.getSelectionModel().leadSelectionRow = "";

        if (!params?.noEmit) {
            // Emit event
            this._smartTable.sortEvent.emit({
                columnsSorted: this._activeSortedColumns
            });
        }
        if (!params?.noRefresh) {
            this._smartTable.refresh("sort");
        }
    }

    /**
     * Callback triggered when click on sortable header
     * Sort column
     */
    public onHeaderSort(evt: MouseEvent, col: SmartTableColumn<ItemData>): void {

        if (this._sortIsOccurring) { return; }

        // If left click on excelLikeSortFilter is enable and interaction is on left click, disable default sort
        const excelLikeSortFilterOptions = this._smartTable.options.excelLikeSortFilter;
        if (excelLikeSortFilterOptions?.enable && excelLikeSortFilterOptions.clickInteraction === "left") {
            this.sortClickEvent.emit({ col, mouseEvent: evt });
            return;
        }

        // Default smart table sort if option enableExcelLikeSortFilter is not active
        try {
            const renderCacheData: RenderCacheData = {};
            this._sortIsOccurring = true;
            const element = evt.target as HTMLTableCellElement;
            const simpleSort = !evt.shiftKey;

            // Get attribute aria-sort value to apply new order sort
            const ariaSortElt = getAllParents(element).find(elt => elt.hasAttribute("aria-sort"));
            const orderBeforeClick = ariaSortElt?.getAttribute("aria-sort") as SmartTableSortsAvailable | null;

            // Reset all header sorts if simple sort
            if (simpleSort) { this._activeSortedColumns = []; }

            this._sortableDivs.forEach((elt) => {
                if (simpleSort) {
                    elt.setAttribute("aria-sort", "none");
                }
                (elt.nextSibling as HTMLElement)?.setAttribute("aria-sort-order", "");
            });

            // Apply new order sort to target column
            if (ariaSortElt && orderBeforeClick) {

                let orderAfterClick = this._sortsAvailable.indexOf(orderBeforeClick) + 1;

                // If current order is last item of array, go back to index 0
                orderAfterClick = orderAfterClick > this._sortsAvailable.length - 1 ? 0 : orderAfterClick;
                const newSortType = this._sortsAvailable[orderAfterClick];
                ariaSortElt.setAttribute("aria-sort", newSortType);

                // Sort table with cell index and aria-sort attribute value
                const closestTh = ariaSortElt.closest("th");
                const columnUid = closestTh?.getAttribute(COLUMN_UID_ATTRIBUTE);
                if (this.columnIsSortable(columnUid ?? "")) {
                    const columnSort = this._activeSortedColumns.find(c => c.column.uid === columnUid);
                    if (columnSort) { // The column is already sorted
                        if (newSortType === "none") {
                            // No sort applied on column so we remove it from active sorted columns array
                            CollectionTools.removeFirstFromArray(this._activeSortedColumns, columnSort);
                        } else {
                            // Update column sort status stype 
                            columnSort.sortType = newSortType;
                        }
                    } else if (newSortType !== "none") { // The column is newly sorted so we add it to the array

                        // If we are here, column exist
                        const column = this._smartTable.options.columns.find(optionCol => optionCol.uid === columnUid);
                        if (column) {
                            this._activeSortedColumns.push({ column, sortType: newSortType, element: ariaSortElt });
                        }
                    }

                    if (!simpleSort) { // Set aria-sort-order attribute for each active sorted column
                        this.setHeaderSortOrderAttributes();
                    }
                    this.sortTable({ renderCacheData });
                }
            }
        } catch (e) {
            this._log?.warn(e);
        } finally {
            this._sortIsOccurring = false;
        }
        this.sortClickEvent.emit({ col, mouseEvent: evt });
    }
}